import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import DrawerShell from '../components/DrawerShell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Boxes, Search, Plus, LogOut, Loader2, Package, AlertTriangle, Flame,
  Clock, CheckCircle2, XCircle, RefreshCw, FileText, ChevronRight, Edit3,
} from 'lucide-react';
import axios from 'axios';
import { formatDateIST } from '../utils/date';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CFG = {
  urgent: { label: 'Urgent', cls: 'bg-red-100 text-red-700 border-red-200 animate-pulse', icon: Flame },
  high: { label: 'High', cls: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle },
  normal: { label: 'Normal', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Package },
  low: { label: 'Low', cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
};

const STATUS_CFG = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  converted: { label: 'Converted to PO', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-500 border-slate-200', icon: XCircle },
};

export default function PharmacyStoresPage() {
  const { user, setUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, converted: 0, cancelled: 0, urgent_pending: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [showRaise, setShowRaise] = useState(false);
  const [showMine, setShowMine] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mine_only: showMine };
      if (filter !== 'all') params.status = filter;
      const [prRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/stores/purchase-requests`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/stores/stats`, { withCredentials: true }),
      ]);
      setRequests(prRes.data || []);
      setStats(statsRes.data || stats);
    } catch (e) {
      toast.error('Failed to load purchase requests');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showMine]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleLogout = async () => {
    try { await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch (_) {}
    setUser(null);
    window.location.assign('/login');
  };

  const filtered = requests.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (r.medicine_name || '').toLowerCase().includes(q)
        || (r.composition || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="text-white shadow-md sticky top-0 z-30" style={{ background: 'linear-gradient(120deg,#040E1A,#071B34,#0C2744)' }}>
        <div className="h-1" style={{ background: 'linear-gradient(90deg,#F15A22,#C4A35A,#F15A22)' }} />
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/iith-logo.png" alt="" className="h-10 w-10 rounded-full bg-white p-1 object-contain" />
            <div>
              <h1 className="text-lg font-semibold leading-tight font-heading">
                Pharmacy Stores
              </h1>
              <p className="text-xs text-[#9AADBE]">IIT Hyderabad Hospital · purchase requests</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium" data-testid="pharmacy-stores-username">{user?.name || 'Staff'}</p>
              <p className="text-[10px] text-white/60 uppercase tracking-wider">Pharmacy Stores</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10 hover:text-white gap-1"
              onClick={handleLogout}
              data-testid="pharmacy-stores-logout"
            >
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pharmacy-stores-stats">
          <StatTile label="Pending" value={stats.pending} icon={Clock} accent="amber" />
          <StatTile label="Urgent / High" value={stats.urgent_pending} icon={Flame} accent="red" />
          <StatTile label="Converted to PO" value={stats.converted} icon={CheckCircle2} accent="emerald" />
          <StatTile label="Cancelled" value={stats.cancelled} icon={XCircle} accent="slate" />
        </div>

        {/* Toolbar */}
        <Card className="mb-4 border-slate-200 shadow-sm">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by medicine or composition..."
                className="pl-9 h-10"
                data-testid="ps-search"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {['pending', 'converted', 'cancelled', 'all'].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  data-testid={`ps-filter-${s}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                    filter === s
                      ? 'bg-amber-700 text-white border-amber-700'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {s === 'all' ? 'All' : (STATUS_CFG[s]?.label || s)}
                </button>
              ))}
              <button
                onClick={() => setShowMine((v) => !v)}
                data-testid="ps-mine-toggle"
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                  showMine
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                Mine only
              </button>
              <Button size="sm" variant="outline" onClick={loadAll} className="gap-1.5" data-testid="ps-refresh">
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
            <RaisePRDialog
              open={showRaise}
              onOpenChange={setShowRaise}
              onCreated={loadAll}
            />
          </CardContent>
        </Card>

        {/* PR list */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {loading && requests.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <Loader2 className="h-7 w-7 animate-spin mx-auto mb-3" />
                Loading purchase requests…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-400" data-testid="ps-empty-state">
                <Package className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm font-medium text-slate-500">No purchase requests found.</p>
                <p className="text-xs mt-1">Raise a new PR to start replenishing stock.</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[60vh]">
                <div className="divide-y divide-slate-100" data-testid="ps-list">
                  {filtered.map((r) => (
                    <PRRow key={r.id} pr={r} />
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, accent }) {
  const tones = {
    amber: 'bg-amber-50 border-amber-100 text-amber-800',
    red: 'bg-red-50 border-red-100 text-red-800',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <Card className={`border ${tones[accent] || tones.slate}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-white/70 flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
          <p className="text-2xl font-bold leading-none">{value ?? 0}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function PRRow({ pr }) {
  const PCfg = PRIORITY_CFG[pr.priority] || PRIORITY_CFG.normal;
  const SCfg = STATUS_CFG[pr.status] || STATUS_CFG.pending;
  const PIcon = PCfg.icon;
  const SIcon = SCfg.icon;
  return (
    <div className="px-5 py-3 hover:bg-slate-50 transition" data-testid={`ps-row-${pr.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-900 truncate">{pr.medicine_name}</p>
            <Badge variant="outline" className={`text-[10px] gap-1 ${PCfg.cls}`}>
              <PIcon className="h-3 w-3" /> {PCfg.label}
            </Badge>
            <Badge variant="outline" className={`text-[10px] gap-1 ${SCfg.cls}`}>
              <SIcon className="h-3 w-3" /> {SCfg.label}
            </Badge>
          </div>
          {pr.composition && (
            <p className="text-[11px] text-blue-700 truncate mt-0.5">{pr.composition}</p>
          )}
          <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500 flex-wrap">
            <span><span className="text-slate-400">Qty:</span> <b className="text-slate-700">{pr.quantity} {pr.unit}</b></span>
            {pr.current_stock !== undefined && pr.current_stock !== null && (
              <span>· <span className="text-slate-400">Stock:</span> <b className={pr.current_stock <= 10 ? 'text-red-600' : 'text-slate-700'}>{pr.current_stock}</b></span>
            )}
            {pr.last_mrp > 0 && <span>· <span className="text-slate-400">Last MRP:</span> <b>₹{pr.last_mrp}</b></span>}
            {pr.raised_by_name && <span>· <span className="text-slate-400">By</span> {pr.raised_by_name}</span>}
            {pr.created_at && <span>· {formatDateIST(pr.created_at)}</span>}
            {pr.converted_po_number && (
              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 font-mono">
                {pr.converted_po_number}
              </Badge>
            )}
          </div>
          {pr.reason && (
            <p className="text-[11px] italic text-slate-500 mt-1 line-clamp-2">"{pr.reason}"</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Raise New PR Dialog ----------------
function RaisePRDialog({ open, onOpenChange, onCreated }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('Nos');
  const [priority, setPriority] = useState('normal');
  const [reason, setReason] = useState('');
  const [allowFreeText, setAllowFreeText] = useState(false);
  const [freeText, setFreeText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uoms, setUoms] = useState(['Nos', 'Tablet', 'Strips', 'Bottle', 'ML', 'MG']);

  // Reset form when reopened
  useEffect(() => {
    if (open) {
      setSearch(''); setResults([]); setPicked(null);
      setQuantity(''); setPriority('normal'); setReason('');
      setAllowFreeText(false); setFreeText(''); setUnit('Nos');
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/purchase/uoms`, { withCredentials: true });
        if (!cancelled && Array.isArray(data?.uoms)) setUoms(data.uoms);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Live medicine search — debounced 300ms
  useEffect(() => {
    if (!search || search.length < 2 || allowFreeText) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/common/medicines`, {
          params: { search }, withCredentials: true,
        });
        setResults(Array.isArray(data) ? data.slice(0, 10) : []);
      } catch (_) {}
    }, 300);
    return () => clearTimeout(t);
  }, [search, allowFreeText]);

  const pickMedicine = (m) => {
    setPicked(m);
    setUnit(m.unit || 'Nos');
    setResults([]);
    setSearch(m.name || '');
  };

  const handleSubmit = async () => {
    const medicineName = allowFreeText ? freeText.trim() : (picked?.name || '');
    if (!medicineName) {
      toast.error('Please pick or type a medicine name');
      return;
    }
    const qty = Number(quantity);
    if (!qty || qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/stores/purchase-requests`, {
        medicine_name: medicineName,
        composition: picked?.composition || '',
        unit,
        quantity: qty,
        priority,
        reason: reason.trim(),
        current_stock: picked?.stock ?? 0,
        last_mrp: 0,
      }, { withCredentials: true });
      toast.success('Purchase request raised');
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to raise PR');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button onClick={() => onOpenChange(true)} className="bg-amber-700 hover:bg-amber-800 gap-1.5" data-testid="ps-raise-btn">
        <Plus className="h-4 w-4" /> Raise New PR
      </Button>
      <DrawerShell
        open={open}
        onClose={() => onOpenChange(false)}
        eyebrow="Stores"
        title="Raise Purchase Request"
        subtitle="Item-level PR from inventory search or free-text"
        accent="#b45309"
        icon={<Plus className="h-5 w-5" />}
        width="lg"
        testId="ps-raise-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-amber-700 hover:bg-amber-800 gap-1.5"
              data-testid="ps-raise-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Submit PR
            </Button>
          </>
        }
      >
        <div className="px-6 py-4 space-y-4">
          {/* Source picker — inventory search vs free text */}
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => { setAllowFreeText(false); setFreeText(''); }}
              data-testid="ps-source-inventory"
              className={`px-3 py-1.5 rounded-full border font-semibold transition ${
                !allowFreeText ? 'bg-amber-700 text-white border-amber-700' : 'bg-white text-slate-600 border-slate-200'
              }`}
            >
              Inventory Search
            </button>
            <button
              onClick={() => { setAllowFreeText(true); setPicked(null); }}
              data-testid="ps-source-freetext"
              className={`px-3 py-1.5 rounded-full border font-semibold transition ${
                allowFreeText ? 'bg-amber-700 text-white border-amber-700' : 'bg-white text-slate-600 border-slate-200'
              }`}
            >
              Free-text
            </button>
          </div>

          {!allowFreeText ? (
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Medicine</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPicked(null); }}
                  placeholder="Search by name or composition..."
                  className="pl-9 h-10"
                  data-testid="ps-raise-search"
                />
                {results.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg">
                    {results.map((m, i) => (
                      <button
                        key={i}
                        onClick={() => pickMedicine(m)}
                        className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b last:border-b-0"
                        data-testid={`ps-raise-result-${i}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-900 truncate">{m.name}</span>
                          {m.stock !== undefined && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              m.stock === 0 ? 'bg-red-100 text-red-700' :
                              m.stock <= 10 ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {m.stock === 0 ? 'OOS' : `Stock ${m.stock}`}
                            </span>
                          )}
                        </div>
                        {m.composition && <p className="text-[10px] text-blue-700 truncate">{m.composition}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {picked && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-100 rounded text-xs" data-testid="ps-raise-picked">
                  <p className="font-semibold text-slate-900">{picked.name}</p>
                  {picked.composition && <p className="text-blue-700">{picked.composition}</p>}
                  <p className="text-slate-500 mt-0.5">
                    Stock: <b>{picked.stock ?? 0}</b> {picked.unit && `· Unit: ${picked.unit}`}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Medicine name (free-text)</Label>
              <Input
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Enter medicine name not in inventory..."
                className="h-10"
                data-testid="ps-raise-freetext"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Use this when the item isn't in the inventory catalogue yet.
              </p>
            </div>
          )}

          {/* Quantity + unit + priority */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Quantity</Label>
              <Input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="e.g. 100"
                min="0"
                className="h-10"
                data-testid="ps-raise-qty"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">UOM</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="h-10" data-testid="ps-raise-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {uoms.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="h-10" data-testid="ps-raise-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Reason / Note (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Stock running out, urgent indent from ward..."
              className="text-sm"
              rows={2}
              data-testid="ps-raise-reason"
            />
          </div>
        </div>
      </DrawerShell>
    </>
  );
}
